import {
  Button,
  DetailList,
  getThemePreference,
  ImagePicker,
  NOTIFICATION_SOUND_KEY,
  NotificationSettings,
  PageHeader,
  PasswordField,
  PushToggle,
  setThemePreference,
  type ThemeChoice,
  usePersistentFlag,
  usePush,
} from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { Dialog, DialogContent } from "@majitap/ui/arc/dialog/dialog";
import { Input } from "@majitap/ui/arc/input/input";
import SegmentedControl from "@majitap/ui/arc/segmented-control/segmented-control";
import { Switch } from "@majitap/ui/arc/switch/switch";
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { type FormEvent, type ReactNode, useState } from "react";
import { accountActions, imageProblem, phoneCodeProblem, phoneProblem } from "../account";
import { errorOf } from "../api";
import { changePasswordProblem, passwordProblem } from "../auth-copy";
import { messageFor } from "../error-copy";
import { ACCOUNT_ITEM } from "../nav";
import { preferencesQuery, pushApi, setPreference } from "../notifications";
import { changePassword, confirmPhone, isAuthError, type Me, meQuery, sendPhoneCode } from "../session";
import styles from "./settings/settings.module.css";

export const initialsOf = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]!.toUpperCase())
    .join("");

/** The page puts the API's new /v1/me straight into the session's cache. */
export function useSetMe() {
  const queryClient = useQueryClient();
  return (me: Me) => queryClient.setQueryData(meQuery.queryKey, me);
}

/** My profile (P8-54): anyone signed in, whatever their role. */
export function AccountPage(): ReactNode {
  // the console's route only renders once someone is signed in
  const me = useSuspenseQuery(meQuery).data!;
  return (
    <>
      <PageHeader title={ACCOUNT_ITEM.label} description={ACCOUNT_ITEM.description} />
      <div className={styles.main}>
        <Profile me={me} />
        <Password />
        <Notifications />
        <Appearance />
      </div>
    </>
  );
}

function Profile({ me }: { me: Me }) {
  const setMe = useSetMe();
  const [editing, setEditing] = useState<"name" | "phone" | null>(null);
  const [error, setError] = useState<string>();
  const run = async (action: () => Promise<Me>) => {
    setError(undefined);
    try {
      setMe(await action());
    } catch (e) {
      const failure = errorOf(e);
      setError(imageProblem(failure.code) ?? messageFor(failure));
      throw e;
    }
  };
  return (
    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>Profile</h2>
      <ImagePicker
        shape="round"
        noun="photo"
        src={me.user.image_url}
        fallback={initialsOf(me.user.name)}
        onUpload={(file) => run(() => accountActions.setPhoto(file))}
        onRemove={() => run(accountActions.removePhoto)}
      />
      {error ? (
        <Alert tone="danger" title="Photo not saved">
          {error}
        </Alert>
      ) : null}
      <section className={styles.card}>
        <DetailList
          items={[
            { label: "Name", value: me.user.name },
            { label: "Email", value: me.user.email },
            { label: "Phone", value: me.user.phone_number },
          ]}
        />
      </section>
      <p className={styles.muted}>Your email is how you sign in. An administrator changes it.</p>
      <div className={styles.actions}>
        <Button variant="secondary" onClick={() => setEditing("name")}>
          Change your name
        </Button>
        <Button variant="secondary" onClick={() => setEditing("phone")}>
          {me.user.phone_number ? "Change your phone" : "Add a phone number"}
        </Button>
      </div>
      {editing === "name" ? <EditName name={me.user.name} onClose={() => setEditing(null)} /> : null}
      {editing === "phone" ? (
        <EditPhone companySlug={me.active_company!.slug} onClose={() => setEditing(null)} />
      ) : null}
    </section>
  );
}

function EditName({ name: current, onClose }: { name: string; onClose: () => void }) {
  const setMe = useSetMe();
  const [name, setName] = useState(current);
  const save = useMutation({
    mutationFn: () => accountActions.rename(name.trim()),
    onSuccess: (me) => {
      setMe(me);
      onClose();
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent title="Change your name" description="Staff and the audit log show this name.">
        <div className={styles.form}>
          <Input label="Name" value={name} maxLength={120} onChange={(e) => setName(e.target.value)} />
          {save.error ? (
            <Alert tone="danger" title="Not saved">
              {messageFor(errorOf(save.error))}
            </Alert>
          ) : null}
          <div className={styles.footer}>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button disabled={!name.trim()} loading={save.isPending} onClick={() => save.mutate()}>
              Save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** A new phone number, confirmed with a code sent to it by SMS. */
function EditPhone({ companySlug, onClose }: { companySlug: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [problem, setProblem] = useState<{ phone?: string; code?: string; alert?: string }>({});
  const [pending, setPending] = useState(false);
  const failure = (error: unknown) => phoneCodeProblem(isAuthError(error) ? error.code : "UNKNOWN");

  const send = async () => {
    const number = phone.replace(/[\s-]/g, "");
    const bad = phoneProblem(number);
    setProblem(bad ? { phone: bad } : {});
    if (bad) return;
    setPending(true);
    try {
      await sendPhoneCode(number, companySlug);
      setSentTo(number);
      setCode("");
    } catch (error) {
      setProblem({ alert: failure(error) });
    } finally {
      setPending(false);
    }
  };

  const confirm = async () => {
    if (!sentTo) return;
    if (!/^\d{4,8}$/.test(code.trim())) return setProblem({ code: "Enter the code from the SMS." });
    setProblem({});
    setPending(true);
    try {
      await confirmPhone(sentTo, code.trim(), companySlug);
      await queryClient.invalidateQueries({ queryKey: meQuery.queryKey });
      onClose();
    } catch (error) {
      const code = isAuthError(error) ? error.code : "UNKNOWN";
      setProblem(code === "INVALID_OTP" ? { code: failure(error) } : { alert: failure(error) });
      setPending(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        title="Change your phone"
        description={
          sentTo ? `We sent a code to ${sentTo}. It works for 5 minutes.` : "We'll send a code by SMS to confirm it."
        }
      >
        <div className={styles.form}>
          {sentTo ? (
            <Input
              label="Code"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              error={problem.code}
              onChange={(e) => setCode(e.target.value)}
            />
          ) : (
            <Input
              label="Phone number"
              type="tel"
              autoComplete="tel"
              placeholder="+254712345678"
              value={phone}
              error={problem.phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          )}
          {problem.alert ? (
            <Alert tone="danger" title="Not confirmed">
              {problem.alert}
            </Alert>
          ) : null}
          <div className={styles.footer}>
            {sentTo ? (
              <Button variant="secondary" disabled={pending} onClick={() => setSentTo(null)}>
                Use another number
              </Button>
            ) : (
              <Button variant="secondary" onClick={onClose}>
                Cancel
              </Button>
            )}
            {sentTo ? (
              <Button loading={pending} onClick={confirm}>
                Confirm
              </Button>
            ) : (
              <Button disabled={!phone.trim()} loading={pending} onClick={send}>
                Send the code
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Password() {
  const [current, setCurrent] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [problem, setProblem] = useState<ReturnType<typeof changePasswordProblem> & { confirm?: string }>({});
  const [pending, setPending] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setDone(false);
    const bad = passwordProblem(password);
    const mismatch = !bad && confirm !== password ? "The two passwords don't match." : undefined;
    setProblem({ password: bad, confirm: mismatch, current: current ? undefined : "Enter your current password." });
    if (bad || mismatch || !current) return;
    setPending(true);
    try {
      await changePassword(current, password);
      setCurrent("");
      setPassword("");
      setConfirm("");
      setProblem({});
      setDone(true);
    } catch (error) {
      setProblem(
        changePasswordProblem(isAuthError(error) ? error.code : "UNKNOWN", isAuthError(error) ? error.message : ""),
      );
    } finally {
      setPending(false);
    }
  };

  return (
    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>Password</h2>
      <form className={styles.form} onSubmit={submit} noValidate>
        <PasswordField
          label="Current password"
          autoComplete="current-password"
          value={current}
          error={problem.current}
          onChange={(e) => setCurrent(e.target.value)}
        />
        <div className={styles.pair}>
          <PasswordField
            label="New password"
            autoComplete="new-password"
            description="At least 8 characters, not only digits."
            value={password}
            error={problem.password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <PasswordField
            label="Confirm the new password"
            autoComplete="new-password"
            value={confirm}
            error={problem.confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        </div>
        {problem.alert ? (
          <Alert tone="danger" title={problem.alert.title}>
            {problem.alert.body}
          </Alert>
        ) : null}
        {done ? (
          <Alert tone="success" title="Password changed">
            You're signed out everywhere else. Use the new password next time you sign in.
          </Alert>
        ) : null}
        <div>
          <Button type="submit" loading={pending}>
            Change the password
          </Button>
        </div>
      </form>
    </section>
  );
}

const THEMES = [
  { value: "light", label: "Light" },
  { value: "dark", label: "Dark" },
  { value: "system", label: "System" },
];

function Appearance() {
  const [theme, setTheme] = useState<ThemeChoice>(getThemePreference);
  return (
    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>Appearance</h2>
      <SegmentedControl
        label="Theme"
        options={THEMES}
        value={theme}
        onValueChange={(value) => {
          setTheme(value as ThemeChoice);
          setThemePreference(value as ThemeChoice);
        }}
      />
      <p className={styles.muted}>Saved in this browser. System follows your device's light or dark setting.</p>
    </section>
  );
}

/**
 * Push in this browser (P11-05), and which alerts come by push (P11-07): staff alerts even when
 * the console's tab is in the background. The bell keeps every alert whatever is chosen here.
 */
function Notifications() {
  const queryClient = useQueryClient();
  const push = usePush(pushApi, "/push-sw.js");
  const prefs = useQuery(preferencesQuery);
  const [error, setError] = useState<string>();
  const [sound, setSound] = usePersistentFlag(NOTIFICATION_SOUND_KEY, true);
  const soundSwitch = <Switch label="Play a sound for new notifications" checked={sound} onCheckedChange={setSound} />;
  if (push.status === "checking" || push.status === "unavailable" || push.status === "unsupported")
    return (
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Notifications</h2>
        {soundSwitch}
        <p className={styles.muted}>
          New notifications pop up at the bottom right while the console is open. Saved in this browser.
        </p>
      </section>
    );
  return (
    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>Notifications</h2>
      {soundSwitch}
      <PushToggle
        {...push}
        description="Your alerts (new orders, incidents, payouts to review) as they happen, even when the console isn't open."
      />
      <NotificationSettings
        topics={prefs.data?.topics}
        error={error ?? (prefs.error ? messageFor(errorOf(prefs.error)) : undefined)}
        onChange={(topic, channel, enabled) => {
          setError(undefined);
          return setPreference(topic, channel, enabled)
            .then((next) => queryClient.setQueryData(preferencesQuery.queryKey, next))
            .catch((e) => setError(messageFor(errorOf(e))));
        }}
      />
      <p className={styles.muted}>Push turned off for a topic still shows its alerts under the bell.</p>
    </section>
  );
}
